import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import "./title";
import "./text";
import "./menu-content";
import { property } from "lit/decorators/property.js";
import { sharedStyles } from "../styles/shared";
import { when } from "lit/directives/when.js";
import { BsMenuContent, MenuAlign, MenuJustify } from "./menu-content";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import {
	setBestPositionForMenu,
	setTooltipDataReady,
	TOOLTIP_CORRECTION_PRIORITY_LIST,
	TooltipCorrectionElement,
	TooltipCorrectionPriority
} from "../util/tooltip-correction-util";
import { query } from "lit/decorators/query.js";

@customElement("bs-tooltip")
export class BsTooltip extends LitElement implements TooltipCorrectionElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					cursor: default;
					position: relative;
					display: inline-flex;
				}

				#content {
				}

				#tooltip {
					--menu-max-width: var(--tooltip-menu-max-width, 240px);
					--menu-width: var(--tooltip-menu-width, max-content);
					--menu-content-padding: var(--space-s) var(--space-m);
					pointer-events: var(--tooltip-pointer-events, none);
					display: var(--tooltip-display, none);
					position: absolute;
					user-select: none;
					-webkit-user-select: none;
					opacity: var(--tooltip-opacity, 0);
					animation: fade-in 120ms ease-out;
					animation-delay: var(--tooltip-delay, 200ms);
					animation-fill-mode: forwards;
				}

				:host([align="bottom"]) #tooltip {
					top: var(--tooltip-offset-v, 100%);
				}

				:host([align="top"]) #tooltip {
					bottom: var(--tooltip-offset-v, 100%);
				}

				:host([justify="left"]) #tooltip {
					right: var(--tooltip-offset-h, 0);
				}

				:host([justify="right"]) #tooltip {
					left: var(--tooltip-offset-h, 0);
				}

				:host([justify="center"]) #tooltip {
					left: var(--tooltip-offset-h, 50%);
					transform: translate(-50%, 0);
				}

				#tooltip {
				}

				:host(:not([data-ready])) #tooltip {
					visibility: hidden;
				}

				#img {
					object-fit: cover;
					width: 100%;
					height: var(--tooltip-img-height, 100px);
					margin: 0 0 var(--space-s);
					border-radius: var(--border-radius-m);
				}

				#title:not(:last-child) {
					margin: 0 0 var(--space-xxs);
				}

				@keyframes fade-in {
					0% {
						opacity: 0;
					}
					100% {
						opacity: 1;
					}
				}

				:host([open]:not([inactive])) #tooltip {
					display: flex;
				}

				@media (hover: hover) {
					:host(:not([inactive]):hover) #tooltip {
						display: flex;
					}
				}
			`
		];
	}

	@property({ type: String }) text?: string;
	@property({ type: String }) img?: string;
	@property({ type: String }) headline?: string;
	@property({ type: String, reflect: true }) align: MenuAlign = "bottom";
	@property({ type: String, reflect: true }) justify: MenuJustify = "center";
	@property({ type: Boolean, reflect: true }) inactive = false;
	@property({ type: Boolean, reflect: true }) open = false;
	@property({ type: Object }) getContainer = () => window;
	@property({ type: Object }) contentTemplate?: () => TemplateResult;
	@property({ type: Boolean, reflect: true, attribute: "no-correction" }) noCorrection = false;

	@query("#tooltip") $menu: BsMenuContent | undefined;

	private tooltipCorrectionPriority: TooltipCorrectionPriority[] = TOOLTIP_CORRECTION_PRIORITY_LIST;
	private boundHandleMouseLeave = this.handleMouseLeave.bind(this);
	private boundHandleMouseEnter = this.handleMouseEnter.bind(this);
	private boundHandleClick = this.handleClick.bind(this);

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);

		this.tooltipCorrectionPriority = [
			{
				align: this.align,
				justify: this.justify
			},
			...TOOLTIP_CORRECTION_PRIORITY_LIST
		];

		this.addEventListener("mouseenter", this.boundHandleMouseEnter);
		this.addEventListener("mouseleave", this.boundHandleMouseLeave);
		this.addEventListener("click", this.boundHandleClick);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener("mouseenter", this.boundHandleMouseEnter);
		this.removeEventListener("mouseleave", this.boundHandleMouseLeave);
		this.removeEventListener("click", this.boundHandleClick);
	}

	protected updated(props: PropertyValues) {
		super.updated(props);

		if (props.has("open") || props.has("inactive")) {
			if (this.open) {
				if (this.noCorrection) {
					setTooltipDataReady(this);
				} else {
					this.correctTooltipPosition();
				}
			}
		}
	}

	correctTooltipPosition() {
		setBestPositionForMenu(this, this.tooltipCorrectionPriority);
	}

	handleMouseEnter() {
		this.open = true;
	}

	handleMouseLeave() {
		this.open = false;
	}

	handleClick() {
		this.open = false;
	}

	render() {
		const { text, img, headline } = this;
		return html`
			<slot></slot>
			${when(
				this.open,
				() => html`
					<bs-menu-content id="tooltip" justify="${this.justify}" align="${this.align}" inert>
						<div id="content">
							${when(img != null, () => html`<img id="img" src="${img}" />`)}
							${when(headline, () => html`<bs-title id="title" size="small">${headline}</bs-title>`)}
							${when(text != null, () => html`<bs-text id="text">${unsafeHTML(text)}</bs-text>`)}
							${when(this.contentTemplate != null, () => this.contentTemplate?.())}
						</div>
					</bs-menu-content>
				`
			)}
		`;
	}
}
